import { BaseApiService } from './base.service';
import {
  User,
  UserCreate,
  UserUpdate,
  Server,
  ServerCreate,
  ServerUpdate,
  Company,
  CompanyCreate,
  CompanyUpdate,
  PricingHeader,
  PricingHeaderCreate,
  PricingHeaderUpdate,
  Plan,
  PlanCreate,
  PlanUpdate,
  PriceList,
  PriceListCreate,
  PriceListUpdate,
  PlanFeature,
  PlanFeatureCreate,
  PlanFeatureUpdate
} from '../../types';

/**
 * Enhanced CRUD Service for all entities
 * Uses the new /api/v1 endpoints with advanced filtering and bulk operations
 */
export class CrudService extends BaseApiService {
  private readonly baseUrl = '/api/v1';

  // ============================================================================
  // USERS CRUD
  // ============================================================================

  async getUsers(params: {
    skip?: number;
    limit?: number;
    server_id?: number;
    company_id?: number;
    is_active?: boolean;
    search?: string;
  } = {}): Promise<User[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/users?${queryParams.toString()}`);
  }

  async getUser(id: number): Promise<User> {
    return this.get(`${this.baseUrl}/users/${id}`);
  }

  async createUser(user: UserCreate): Promise<User> {
    return this.post(`${this.baseUrl}/users`, user);
  }

  async updateUser(id: number, user: UserUpdate): Promise<User> {
    return this.put(`${this.baseUrl}/users/${id}`, user);
  }

  async deleteUser(id: number, softDelete: boolean = true): Promise<{ message: string }> {
    const params = softDelete ? '?soft_delete=true' : '?soft_delete=false';
    return this.delete(`${this.baseUrl}/users/${id}${params}`);
  }

  async bulkDeleteUsers(userIds: number[], softDelete: boolean = true): Promise<{ message: string }> {
    const params = softDelete ? '?soft_delete=true' : '?soft_delete=false';
    return this.post(`${this.baseUrl}/bulk/users/delete${params}`, userIds);
  }

  // ============================================================================
  // SERVERS CRUD
  // ============================================================================

  async getServers(params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    search?: string;
  } = {}): Promise<Server[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/servers?${queryParams.toString()}`);
  }

  async getServer(id: number): Promise<Server> {
    return this.get(`${this.baseUrl}/servers/${id}`);
  }

  async createServer(server: ServerCreate): Promise<Server> {
    return this.post(`${this.baseUrl}/servers`, server);
  }

  async updateServer(id: number, server: ServerUpdate): Promise<Server> {
    return this.put(`${this.baseUrl}/servers/${id}`, server);
  }

  async deleteServer(id: number, force: boolean = false): Promise<{ message: string }> {
    const params = force ? '?force=true' : '';
    return this.delete(`${this.baseUrl}/servers/${id}${params}`);
  }

  // ============================================================================
  // COMPANIES CRUD
  // ============================================================================

  async getCompanies(params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    search?: string;
    language?: string;
  } = {}): Promise<Company[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/companies?${queryParams.toString()}`);
  }

  async getCompany(id: number): Promise<Company> {
    return this.get(`${this.baseUrl}/companies/${id}`);
  }

  async createCompany(company: CompanyCreate): Promise<Company> {
    return this.post(`${this.baseUrl}/companies`, company);
  }

  async updateCompany(id: number, company: CompanyUpdate): Promise<Company> {
    return this.put(`${this.baseUrl}/companies/${id}`, company);
  }

  async deleteCompany(id: number, softDelete: boolean = true): Promise<{ message: string }> {
    const params = softDelete ? '?soft_delete=true' : '?soft_delete=false';
    return this.delete(`${this.baseUrl}/companies/${id}${params}`);
  }

  async bulkActivateCompanies(companyIds: number[], isActive: boolean = true): Promise<{ message: string }> {
    const params = `?is_active=${isActive}`;
    return this.post(`${this.baseUrl}/bulk/companies/activate${params}`, companyIds);
  }

  // ============================================================================
  // PRICING HEADERS CRUD
  // ============================================================================

  async getPricingHeaders(params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    category?: string;
    region?: string;
    is_standard?: boolean;
  } = {}): Promise<PricingHeader[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/pricing-headers?${queryParams.toString()}`);
  }

  async getPricingHeader(id: number): Promise<PricingHeader> {
    return this.get(`${this.baseUrl}/pricing-headers/${id}`);
  }

  async createPricingHeader(header: PricingHeaderCreate): Promise<PricingHeader> {
    return this.post(`${this.baseUrl}/pricing-headers`, header);
  }

  async updatePricingHeader(id: number, header: PricingHeaderUpdate): Promise<PricingHeader> {
    return this.put(`${this.baseUrl}/pricing-headers/${id}`, header);
  }

  async deletePricingHeader(id: number): Promise<{ message: string }> {
    return this.delete(`${this.baseUrl}/pricing-headers/${id}`);
  }

  // ============================================================================
  // PLANS CRUD
  // ============================================================================

  async getPlans(params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    search?: string;
  } = {}): Promise<Plan[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/plans?${queryParams.toString()}`);
  }

  async getPlan(id: number): Promise<Plan> {
    return this.get(`${this.baseUrl}/plans/${id}`);
  }

  async createPlan(plan: PlanCreate): Promise<Plan> {
    return this.post(`${this.baseUrl}/plans`, plan);
  }

  async updatePlan(id: number, plan: PlanUpdate): Promise<Plan> {
    return this.put(`${this.baseUrl}/plans/${id}`, plan);
  }

  async deletePlan(id: number): Promise<{ message: string }> {
    return this.delete(`${this.baseUrl}/plans/${id}`);
  }

  // ============================================================================
  // PLAN FEATURES CRUD
  // ============================================================================

  async getPlanFeatures(planId: number, params: {
    feature_type?: string;
    is_active?: boolean;
  } = {}): Promise<PlanFeature[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const query = queryParams.toString();
    const url = `${this.baseUrl}/plans/${planId}/features${query ? `?${query}` : ''}`;
    return this.get(url);
  }

  async createPlanFeature(planId: number, feature: PlanFeatureCreate): Promise<PlanFeature> {
    return this.post(`${this.baseUrl}/plans/${planId}/features`, feature);
  }

  async updatePlanFeature(planId: number, featureId: number, feature: PlanFeatureUpdate): Promise<PlanFeature> {
    return this.put(`${this.baseUrl}/plans/${planId}/features/${featureId}`, feature);
  }

  async deletePlanFeature(planId: number, featureId: number): Promise<{ message: string }> {
    return this.delete(`${this.baseUrl}/plans/${planId}/features/${featureId}`);
  }

  // ============================================================================
  // PRICE LISTS CRUD
  // ============================================================================

  async getPriceLists(params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    currency?: string;
    country?: string;
    plan_id?: number;
    pricing_header_id?: number;
  } = {}): Promise<PriceList[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get(`${this.baseUrl}/price-lists?${queryParams.toString()}`);
  }

  async getPriceList(id: number): Promise<PriceList> {
    return this.get(`${this.baseUrl}/price-lists/${id}`);
  }

  async createPriceList(priceList: PriceListCreate): Promise<PriceList> {
    return this.post(`${this.baseUrl}/price-lists`, priceList);
  }

  async updatePriceList(id: number, priceList: PriceListUpdate): Promise<PriceList> {
    return this.put(`${this.baseUrl}/price-lists/${id}`, priceList);
  }

  async deletePriceList(id: number): Promise<{ message: string }> {
    return this.delete(`${this.baseUrl}/price-lists/${id}`);
  }

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  async getOverviewStats(): Promise<{
    users: {
      total: number;
      active: number;
      deleted: number;
    };
    servers: {
      total: number;
      active: number;
    };
    companies: {
      total: number;
      active: number;
    };
    pricing: {
      headers: number;
      plans: number;
      price_lists: number;
    };
  }> {
    return this.get(`${this.baseUrl}/stats/overview`);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Search across multiple entity types
   */
  async globalSearch(query: string, entityTypes: string[] = ['users', 'companies', 'plans']): Promise<{
    users?: User[];
    companies?: Company[];
    plans?: Plan[];
  }> {
    const results: any = {};

    const searchPromises = entityTypes.map(async (entityType) => {
      try {
        switch (entityType) {
          case 'users':
            results.users = await this.getUsers({ search: query, limit: 10 });
            break;
          case 'companies':
            results.companies = await this.getCompanies({ search: query, limit: 10 });
            break;
          case 'plans':
            results.plans = await this.getPlans({ search: query, limit: 10 });
            break;
        }
      } catch (error) {
        console.error(`Error searching ${entityType}:`, error);
      }
    });

    await Promise.all(searchPromises);
    return results;
  }

  /**
   * Get entity counts for dashboard
   */
  async getEntityCounts(): Promise<{
    users: number;
    servers: number;
    companies: number;
    pricingHeaders: number;
    plans: number;
    priceLists: number;
  }> {
    const stats = await this.getOverviewStats();
    return {
      users: stats.users.total,
      servers: stats.servers.total,
      companies: stats.companies.total,
      pricingHeaders: stats.pricing.headers,
      plans: stats.pricing.plans,
      priceLists: stats.pricing.price_lists
    };
  }

  /**
   * Validate entity before creation/update
   */
  validateEntity(entityType: string, data: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    switch (entityType) {
      case 'user':
        if (!data.username) errors.push('Username is required');
        if (!data.email) errors.push('Email is required');
        if (data.email && !this.isValidEmail(data.email)) errors.push('Invalid email format');
        break;
      case 'company':
        if (!data.name) errors.push('Company name is required');
        break;
      case 'plan':
        if (!data.name) errors.push('Plan name is required');
        break;
      case 'priceList':
        if (!data.currency) errors.push('Currency is required');
        if (!data.plan_id) errors.push('Plan ID is required');
        if (!data.pricing_header_id) errors.push('Pricing header ID is required');
        break;
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export const crudService = new CrudService();